import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  AlertTriangle,
  Camera,
  History,
  Inbox,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { useCaptures, type UserCapture } from "@/stores/useCaptures";
import { SnapshotHistoryPopover } from "@/lib/SnapshotHistoryPopover";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import {
  getStressTest,
  STRESS_TEST_ADOPTION_DEFINITION,
  STRESS_TEST_DESCRIPTION,
  STRESS_TEST_FUD_DEFINITION,
  STRESS_TEST_HIGH_LABEL,
  STRESS_TEST_HOW_TO_USE,
  STRESS_TEST_LOW_LABEL,
  STRESS_TEST_SPECTRUM_LABEL,
  STRESS_TEST_TITLE,
  STRESS_ZONES,
  type StressCard,
  type StressZone,
  type StressZoneId,
} from "./stress-test-content";
import { useCardPlacement } from "./use-card-placement";
import { useCustomStressCards } from "./use-custom-stress-cards";
import { useStressTestSnapshot } from "./useSnapshot";

/** Custom cards (added via "Add your own card") carry this id prefix —
 *  used to decide whether a card gets a remove affordance. */
const CUSTOM_CARD_PREFIX = "custom-";

/**
 * WS4 Stress-Test Role Play activity surface.
 *
 * Vanilla source:
 *   - Layout (objective panel + spectrum board + zones + unplaced
 *     deck): `_legacy/wd.html` lines 1052-1179.
 *   - DOM drag-drop wiring (HTML5 drag events): `_legacy/xaes-nav-wd.js`
 *     lines 672-743.
 *   - Copy + zone definitions: see `./stress-test-content.ts`.
 *
 * Phase 2 (re-implementation) trades the HTML5 drag-events for
 * `@dnd-kit/core` to get accessible keyboard drag-drop for free —
 * tab a card, Space/Enter to pick up, arrows to navigate between
 * droppables, Space/Enter to drop, Esc to cancel.
 *
 * Each card carries a small accent swatch that derives from the zone
 * it currently occupies (unplaced = neutral). Vanilla cards had no
 * per-card classifier, so the swatch is purely a placement read-out
 * rather than invented metadata.
 */

const DECK_DROPPABLE_ID = "deck" as const;

/** Stable empty-deck reference for unauthored workshops (keeps the
 *  placement memo from re-running on a fresh `[]` each render). */
const NO_CARDS: readonly StressCard[] = [];

/**
 * Map each zone accent token to its legacy CSS variable family. We
 * compose all chrome (border / bg / text / swatch / focus ring) inline
 * from `--accent-<name>` + `--accent-<name>-rgb`, matching how the
 * vanilla `#stressTestModal` markup was authored at
 * `_legacy/wd.html` lines 1147-1158.
 */
type ZoneAccentVars = {
  color: string;
  rgb: string;
};

const ZONE_ACCENT_VARS: Record<StressZone["accent"], ZoneAccentVars> = {
  red: { color: "var(--accent-red)", rgb: "var(--accent-red-rgb)" },
  gold: { color: "var(--accent-gold)", rgb: "var(--accent-gold-rgb)" },
  sky: { color: "var(--accent-sky)", rgb: "var(--accent-sky-rgb)" },
  green: { color: "var(--accent-green)", rgb: "var(--accent-green-rgb)" },
};

const ZONE_ALIGN_STYLE: Record<StressZone["align"], CSSProperties> = {
  left: { textAlign: "left", justifyContent: "flex-start" },
  center: { textAlign: "center", justifyContent: "center" },
  right: { textAlign: "right", justifyContent: "flex-end" },
};

type StressTestProps = {
  workshopId: string;
  /** Render a CLOSE pill in the top bar that fires this callback. */
  onClose?: () => void;
};

type DraggableCardProps = {
  card: StressCard;
  zone: StressZoneId | null;
  /** Present only for user-added custom cards — renders a remove "x". */
  onRemove?: (cardId: string) => void;
};

function DraggableCard({ card, zone, onRemove }: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: card.id });

  const accent = zone ? ZONE_ACCENT_VARS[zoneAccent(zone)] : null;
  const transformStyle: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};

  // Card chrome takes the zone accent once placed — bg/border/text all
  // tinted to match. Mirrors the legacy `#zone-<id> .st-card` cascade
  // at `_legacy/xaes.css` lines 1313-1316: tinted bg ~0.10, tinted
  // border ~0.30, tinted text via `--accent-<name>`. Unplaced cards
  // keep neutral surface tokens, like the legacy default `.st-card`.
  const placedBg = accent ? `rgba(${accent.rgb}, 0.10)` : "var(--bg-card)";
  const placedBorder = accent
    ? `1px solid rgba(${accent.rgb}, 0.35)`
    : "1px solid hsl(var(--border))";
  const placedColor = accent ? accent.color : "var(--text-primary)";

  const cardStyle: CSSProperties = {
    ...transformStyle,
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 8,
    background: placedBg,
    border: placedBorder,
    color: placedColor,
    fontSize: "var(--fs-base)",
    lineHeight: 1.5,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    boxShadow: isDragging
      ? `0 4px 14px rgba(var(--accent-true-blue-rgb), 0.20), 0 0 0 2px rgba(${
          accent?.rgb ?? "var(--accent-true-blue-rgb)"
        }, 0.55)`
      : "0 1px 2px rgba(0, 0, 0, 0.08)",
    opacity: isDragging ? 0.92 : 1,
    transition: isDragging
      ? "none"
      : "background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s",
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={cardStyle}
      // dnd-kit's `attributes` already supplies `role` +
      // `aria-roledescription`. We only inject a contextual
      // `aria-label` so screen readers announce the card text + its
      // current placement state.
      aria-label={`Card: ${card.text}${zone ? `. Placed in ${zone}.` : ". Not yet placed."}`}
      {...listeners}
      {...attributes}
    >
      <span
        aria-hidden
        style={{
          marginTop: 4,
          flexShrink: 0,
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: accent ? accent.color : "var(--text-muted)",
        }}
      />
      <span style={{ flex: 1 }}>{card.text}</span>
      {onRemove && (
        <button
          type="button"
          // Drag listeners are on the card wrapper — stop the pointer
          // event here so clicking the remove "x" doesn't start a drag.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(card.id);
          }}
          aria-label="Remove this card"
          title="Remove this card"
          style={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            marginTop: 1,
            padding: 0,
            border: "none",
            borderRadius: 4,
            background: "transparent",
            color: "var(--text-muted)",
            cursor: "pointer",
          }}
        >
          <X aria-hidden strokeWidth={2} style={{ width: 11, height: 11 }} />
        </button>
      )}
    </div>
  );
}

function zoneAccent(zone: StressZoneId): StressZone["accent"] {
  const z = STRESS_ZONES.find((entry) => entry.id === zone);
  return z ? z.accent : "sky";
}

type ZoneColumnProps = {
  zone: StressZone;
  cards: StressCard[];
  onRemoveCard: (cardId: string) => void;
};

function ZoneColumn({ zone, cards, onRemoveCard }: ZoneColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: zone.id });
  const accent = ZONE_ACCENT_VARS[zone.accent];

  // Drop-over feedback restores the legacy `.st-zone.drag-over` look
  // (`_legacy/xaes.css` line 1294): bright border + a visible
  // background wash. We tint with the zone's accent rather than the
  // legacy neutral white so the feedback also reinforces *which* zone
  // is about to receive the card — a small upgrade on top of the
  // verbatim port. The inset ring stays for an extra shove of contrast.
  const style: CSSProperties = {
    minHeight: 160,
    padding: 8,
    borderRadius: 8,
    border: isOver
      ? `1px dashed rgba(${accent.rgb}, 0.75)`
      : `1px dashed rgba(${accent.rgb}, 0.30)`,
    background: isOver
      ? `rgba(${accent.rgb}, 0.14)`
      : `rgba(${accent.rgb}, 0.04)`,
    boxShadow: isOver
      ? `inset 0 0 0 2px rgba(${accent.rgb}, 0.55)`
      : undefined,
    transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
  };

  return (
    <div
      ref={setNodeRef}
      className="flex flex-col gap-2"
      style={style}
      aria-label={`${zone.label} drop zone`}
      role="region"
    >
      <div
        className="flex items-center"
        style={{
          gap: 6,
          fontSize: "var(--fs-label)",
          fontWeight: 700,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: accent.color,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: accent.color,
          }}
        />
        <span>{zone.label}</span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fs-label)",
            color: "var(--text-muted)",
            letterSpacing: 1,
          }}
        >
          {cards.length}
        </span>
      </div>
      <div className="flex flex-1 flex-col" style={{ gap: 6 }}>
        {cards.length === 0 ? (
          <span
            style={{
              marginTop: 4,
              textAlign: "center",
              fontSize: "var(--fs-label)",
              letterSpacing: 1,
              textTransform: "uppercase",
              color: `rgba(${accent.rgb}, 0.45)`,
            }}
          >
            Drop here
          </span>
        ) : (
          cards.map((card) => (
            <DraggableCard
              key={card.id}
              card={card}
              zone={zone.id}
              onRemove={
                card.id.startsWith(CUSTOM_CARD_PREFIX) ? onRemoveCard : undefined
              }
            />
          ))
        )}
      </div>
    </div>
  );
}

type UnplacedDeckProps = {
  cards: StressCard[];
  onRemoveCard: (cardId: string) => void;
};

function UnplacedDeck({ cards, onRemoveCard }: UnplacedDeckProps) {
  const { setNodeRef, isOver } = useDroppable({ id: DECK_DROPPABLE_ID });

  const style: CSSProperties = {
    minHeight: 64,
    padding: 10,
    borderRadius: 8,
    border: isOver
      ? "1px dashed rgba(var(--accent-true-blue-rgb), 0.65)"
      : "1px dashed hsl(var(--border))",
    background: isOver
      ? "rgba(var(--accent-true-blue-rgb), 0.12)"
      : "rgba(var(--accent-true-blue-rgb), 0.04)",
    boxShadow: isOver
      ? "inset 0 0 0 2px rgba(var(--accent-true-blue-rgb), 0.45)"
      : undefined,
    transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
  };

  return (
    <div
      ref={setNodeRef}
      className="flex flex-wrap"
      style={{ ...style, gap: 8 }}
      aria-label="Unplaced cards"
      role="region"
    >
      {cards.length === 0 ? (
        <div
          className="flex w-full items-center justify-center"
          style={{
            gap: 6,
            fontSize: "var(--fs-base)",
            color: "var(--text-muted)",
          }}
        >
          <Inbox
            aria-hidden
            strokeWidth={1.5}
            style={{ width: 14, height: 14 }}
          />
          <span>All cards placed</span>
        </div>
      ) : (
        cards.map((card) => (
          <div key={card.id} style={{ width: "min(280px, 100%)" }}>
            <DraggableCard
              card={card}
              zone={null}
              onRemove={
                card.id.startsWith(CUSTOM_CARD_PREFIX) ? onRemoveCard : undefined
              }
            />
          </div>
        ))
      )}
    </div>
  );
}

export function StressTest({ workshopId, onClose }: StressTestProps) {
  const { placements, place, reset, replaceAll } = useCardPlacement(workshopId);
  const { customCards, addCard, removeCard } = useCustomStressCards(workshopId);

  // RT-CANVAS — read the deck through the per-workshop getter so the board
  // generalises beyond WS4, then append any facilitator-added custom
  // cards. `longTitle` de-hardcodes the footer.
  const authoredCards = getStressTest(workshopId) ?? NO_CARDS;
  const cards = useMemo(
    () => (customCards.length === 0 ? authoredCards : [...authoredCards, ...customCards]),
    [authoredCards, customCards],
  );
  const longTitle =
    WORKSHOPS.find((w) => w.id === workshopId)?.longTitle ?? workshopId;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor),
  );

  /* ---- Snapshot system ---------------------------------------------
   *
   * Wires the shared snapshot infra to this board:
   *   - `boardRef` is the outer DOM container `html-to-image` rasters.
   *   - `placements` is serialised into `stateJson` so restoring is
   *     a deterministic map swap; the live drag-drop state mirrors
   *     back to idb-keyval via the existing useCardPlacement effect.
   *   - Auto-save debounces 3s after the most recent placement. A
   *     ref-based timer keeps StrictMode's double-mount honest.
   */
  const boardRef = useRef<HTMLDivElement | null>(null);
  const applyRestoredPlacements = useCallback(
    (next: Record<string, StressZoneId | null>) => {
      replaceAll(next);
    },
    [replaceAll],
  );
  const snapshot = useStressTestSnapshot({
    workshopId,
    boardRef,
    placements,
    applyRestoredPlacements,
  });
  const auth = useAuth();

  /**
   * Auto-save debounce — fires 3s after the most recent placement
   * change. Tracked in a ref so StrictMode's double-mount tears
   * down the timer cleanly without leaking writes.
   *
   * `placementSignature` reduces the map down to a compact string so
   * the effect only refires when the actual data changes, not on
   * every re-render that produced an identical map.
   */
  const placementSignature = useMemo(
    () =>
      Object.entries(placements)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => `${k}=${v}`)
        .sort()
        .join("|"),
    [placements],
  );
  const autoSaveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (placementSignature.length === 0) {
      // Empty board — skip auto-saving the blank state.
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }
    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      void snapshot.saveAuto();
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
    // `snapshot.saveAuto` identity changes whenever the placement map
    // reference changes (it captures the latest map for stateJson),
    // but we only want to debounce on the *signature* — adding it to
    // deps would cause the timer to reset on render thrash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementSignature]);

  const handleManualSave = useCallback(async () => {
    const meta = await snapshot.saveManual();
    if (!meta) {
      toast({
        title: "Snapshot failed",
        description: "Couldn't capture the board — try again.",
      });
      return;
    }
    const stamp = new Date(meta.createdAt).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    const timestamp = new Date(meta.createdAt).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";
    // Migration note: routes to `useCaptures` (the WS4 Captures grid)
    // rather than `useAssetLocker` (Deliverables chip list). See
    // `useCaptures.ts` for the rationale + matches HandshakeBoard.
    const capture: UserCapture = {
      id: meta.id,
      workshopId,
      title: `Stress Test snapshot — ${stamp}`,
      activity: "Activity 2 — STR",
      timestamp,
      kind: "screen",
      accent: "gold",
      source: "Saved",
      caption: `Stress Test board snapshot saved by ${addedBy}.`,
      blobKey: meta.blobKey,
      mimeType: "image/png",
      addedAt: meta.createdAt,
      addedBy,
    };
    // See HandshakeBoard.tsx — same `Workshop["id"]` narrowing
    // posture: the board accepts arbitrary scopes, the store
    // sanitiser drops anything that isn't a real workshop slot.
    useCaptures
      .getState()
      .add(workshopId as Workshop["id"], capture);
    toast({
      title: "Snapshot saved",
      description: capture.title,
    });
  }, [snapshot, auth.user, workshopId]);

  const { cardsByZone, unplaced } = useMemo(() => {
    const grouped: Record<StressZoneId, StressCard[]> = {
      resist: [],
      concern: [],
      open: [],
      embrace: [],
    };
    const deck: StressCard[] = [];
    for (const card of cards) {
      const zone = placements[card.id];
      if (zone && zone in grouped) {
        grouped[zone].push(card);
      } else {
        deck.push(card);
      }
    }
    return { cardsByZone: grouped, unplaced: deck };
  }, [placements, cards]);

  const placedCount = cards.length - unplaced.length;

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over) return;
    const cardId = String(active.id);
    if (over.id === DECK_DROPPABLE_ID) {
      place(cardId, null);
      return;
    }
    const zoneId = STRESS_ZONES.find((z) => z.id === over.id);
    if (zoneId) {
      place(cardId, zoneId.id);
    }
  }

  return (
    <div
      className="flex h-full w-full flex-col"
      style={{ background: "var(--bg-panel)" }}
    >
      {/* Combined top bar — title + SAVE TO CAPTURES + CLOSE. Mirrors
          the legacy `bar-top` row at `_legacy/wd.html` lines 1057-1070
          where the action set was deliberately minimal (only the green
          save + close) so the spectrum below claims the visual focus. */}
      <div
        className="flex flex-shrink-0 flex-wrap items-center justify-between"
        style={{
          gap: 12,
          padding: "10px 14px",
          background: "var(--bg-panel)",
          borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
        }}
      >
        <div className="flex items-center" style={{ gap: 10 }}>
          <AlertTriangle
            aria-hidden
            style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
          />
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "var(--fs-sub)",
              fontWeight: 700,
              letterSpacing: 2,
              color: "var(--text-primary)",
            }}
          >
            STRESS-TEST ROLE PLAY
          </span>
          <span
            style={{
              padding: "1px 8px",
              background: "rgba(var(--accent-gold-rgb), 0.12)",
              border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
              borderRadius: 10,
              fontSize: "var(--fs-label)",
              color: "var(--accent-gold)",
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            FUD × ADOPTION
          </span>
        </div>

        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          <button
            type="button"
            onClick={() => void handleManualSave()}
            title="Save current board to Asset Locker"
            aria-label="Save snapshot to Asset Locker"
            style={SAVE_TO_CAPTURES_STYLE}
            onMouseEnter={onSaveToCapturesHover}
            onMouseLeave={onSaveToCapturesLeave}
          >
            <Camera
              aria-hidden
              strokeWidth={1.8}
              style={{ width: 12, height: 12 }}
            />
            SAVE TO CAPTURES
          </button>
          <SnapshotHistoryPopover
            snapshots={snapshot.snapshots}
            onRestore={(id) => void snapshot.restore(id)}
            onRemove={(id) => void snapshot.remove(id)}
          >
            <button
              type="button"
              title="Browse snapshot history"
              aria-label="Browse snapshot history"
              style={TOP_BTN_STYLE}
              onMouseEnter={onTopBtnHover}
              onMouseLeave={onTopBtnLeave}
            >
              <History
                aria-hidden
                strokeWidth={1.8}
                style={{ width: 12, height: 12 }}
              />
              HISTORY
              {snapshot.snapshots.length > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    padding: "0 5px",
                    borderRadius: 6,
                    background:
                      "rgba(var(--accent-true-blue-rgb), 0.18)",
                    color: "var(--accent-true-blue)",
                    fontSize: "var(--fs-micro)",
                    lineHeight: 1.4,
                  }}
                >
                  {snapshot.snapshots.length}
                </span>
              )}
            </button>
          </SnapshotHistoryPopover>
          <button
            type="button"
            onClick={reset}
            disabled={placedCount === 0}
            aria-label="Reset board — return all cards to the unplaced deck"
            title="Reset board — return all cards to the unplaced deck"
            style={TOP_RESET_BTN_STYLE}
            onMouseEnter={onResetHover}
            onMouseLeave={onResetLeave}
          >
            <RotateCcw
              aria-hidden
              strokeWidth={1.8}
              style={{ width: 12, height: 12 }}
            />
            RESET BOARD
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="modal-close-btn"
              aria-label="Close"
            >
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          )}
        </div>
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        {/* Body: 260 px left info rail + spectrum board on the right.
            Matches the legacy layout at `_legacy/wd.html` lines
            1073-1168: a fixed-width info column (border-right divider)
            and the spectrum/zones/unplaced deck on the right. */}
        <div className="activity-body flex min-h-0 flex-1 overflow-hidden">
          <aside
            className="activity-rail flex h-full flex-col overflow-hidden"
            style={{
              padding: "10px 12px",
              gap: 8,
              background: "var(--bg-panel)",
            }}
          >
            <section style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={SECTION_EYEBROW_STYLE}>Objective</div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "var(--fs-sub)",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  lineHeight: 1.25,
                  color: "var(--text-primary)",
                }}
              >
                {STRESS_TEST_TITLE}
              </div>
              <p
                style={{
                  fontSize: "var(--fs-label)",
                  lineHeight: 1.35,
                  color: "var(--text-secondary)",
                  margin: 0,
                }}
              >
                {STRESS_TEST_DESCRIPTION}
              </p>
            </section>

            <Divider />

            <section style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={SECTION_EYEBROW_STYLE}>How to use</div>
              <ol
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {STRESS_TEST_HOW_TO_USE.map((step, i) => (
                  <li
                    key={step}
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "flex-start",
                      fontSize: "var(--fs-label)",
                      lineHeight: 1.35,
                      color: "var(--text-secondary)",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        flexShrink: 0,
                        color: "var(--accent-gold)",
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <Divider />

            <section style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={SECTION_EYEBROW_STYLE}>FUD</div>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-label)",
                  lineHeight: 1.35,
                  color: "var(--text-muted)",
                }}
              >
                {STRESS_TEST_FUD_DEFINITION}
              </p>
            </section>
            <section style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={SECTION_EYEBROW_STYLE}>Adoption</div>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-label)",
                  lineHeight: 1.35,
                  color: "var(--text-muted)",
                }}
              >
                {STRESS_TEST_ADOPTION_DEFINITION}
              </p>
            </section>
          </aside>

          {/* `boardRef` attaches here — the snapshot rasteriser
              captures the spectrum + zone grid + unplaced deck as a
              single PNG. The left info rail is excluded so snapshots
              focus on the actual exercise output. */}
          <div
            ref={boardRef}
            className="flex min-w-0 flex-1 flex-col overflow-hidden"
          >
            <div
              className="flex flex-col flex-shrink-0"
              style={{ gap: 6, padding: "14px 20px 0" }}
            >
              <div
                className="flex items-center justify-between"
                style={{
                  fontSize: "var(--fs-label)",
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                }}
              >
                <span style={{ color: "var(--accent-red)" }}>
                  {STRESS_TEST_HIGH_LABEL}
                </span>
                <span style={{ color: "var(--text-muted)" }}>
                  {STRESS_TEST_SPECTRUM_LABEL}
                </span>
                <span style={{ color: "var(--accent-green)" }}>
                  {STRESS_TEST_LOW_LABEL}
                </span>
              </div>
              <div
                aria-hidden
                style={{
                  height: 6,
                  borderRadius: 8,
                  background:
                    "linear-gradient(90deg, rgba(var(--accent-red-rgb), 0.70) 0%, rgba(var(--accent-gold-rgb), 0.60) 40%, rgba(var(--accent-true-blue-rgb), 0.50) 65%, rgba(var(--accent-green-rgb), 0.70) 100%)",
                }}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 0,
                }}
              >
                {STRESS_ZONES.map((zone) => {
                  const accent = ZONE_ACCENT_VARS[zone.accent];
                  return (
                    <div
                      key={`label-${zone.id}`}
                      className="flex"
                      style={{
                        ...ZONE_ALIGN_STYLE[zone.align],
                        fontSize: "var(--fs-micro)",
                        letterSpacing: 1.4,
                        textTransform: "uppercase",
                        color: accent.color,
                        opacity: 0.7,
                      }}
                    >
                      {zone.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="flex-1 min-h-0"
              style={{
                display: "grid",
                gap: 8,
                gridTemplateColumns: "repeat(4, 1fr)",
                padding: "8px 20px 0",
              }}
            >
              {STRESS_ZONES.map((zone) => (
                <ZoneColumn
                  key={zone.id}
                  zone={zone}
                  cards={cardsByZone[zone.id]}
                  onRemoveCard={removeCard}
                />
              ))}
            </div>

            <div
              className="flex-shrink-0"
              style={{
                padding: "12px 20px",
                borderTop: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
                marginTop: 12,
              }}
            >
              <div
                className="flex flex-wrap items-center justify-between"
                style={{ gap: 8, marginBottom: 8 }}
              >
                <div
                  style={{
                    fontSize: "var(--fs-label)",
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                  }}
                >
                  {cards.length === 0
                    ? "No stress-test deck for this workshop"
                    : "Unplaced cards — drag to position above"}
                </div>
                <AddCardForm onAdd={addCard} />
              </div>
              {cards.length === 0 ? (
                <p
                  style={{
                    fontSize: "var(--fs-base)",
                    lineHeight: 1.6,
                    color: "var(--text-muted)",
                    margin: 0,
                  }}
                >
                  No FUD×Adoption deck is configured for <code>{workshopId}</code>{" "}
                  yet. Add your own card above to get started.
                </p>
              ) : (
                <UnplacedDeck cards={unplaced} onRemoveCard={removeCard} />
              )}
            </div>
          </div>
        </div>
      </DndContext>

      {/* Footer (legacy bar-btm): breadcrumb + placed counter. */}
      <div
        className="flex flex-shrink-0 items-center"
        style={{
          gap: 8,
          padding: "8px 14px",
          background: "var(--bg-panel)",
          borderTop: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
        }}
      >
        <span
          style={{
            fontSize: "var(--fs-label)",
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          XAES Phase Zero
        </span>
        <span
          aria-hidden
          style={{
            fontSize: "var(--fs-label)",
            color: "rgba(var(--accent-true-blue-rgb), 0.4)",
          }}
        >
          |
        </span>
        <span
          style={{
            fontSize: "var(--fs-label)",
            letterSpacing: 1,
            color: "var(--accent-true-blue)",
          }}
        >
          {workshopId} — {longTitle}
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fs-label)",
            letterSpacing: 1,
            color: "var(--text-muted)",
          }}
        >
          {placedCount} / {cards.length} PLACED
        </span>
      </div>
    </div>
  );
}

type AddCardFormProps = {
  onAdd: (text: string) => void;
};

/**
 * "Add your own card" — a facilitator can type free text and drop it
 * into the unplaced deck as a real `StressCard`, draggable to any zone
 * like an authored one. Lets any workshop's Stress-Test activity be
 * used for a discussion that isn't covered by the authored deck.
 */
function AddCardForm({ onAdd }: AddCardFormProps) {
  const [value, setValue] = useState("");

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setValue("");
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex items-center"
      style={{ gap: 6 }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add your own card…"
        aria-label="Add your own stress-test card"
        style={{
          width: 220,
          padding: "4px 8px",
          borderRadius: 6,
          border: "1px solid hsl(var(--border))",
          background: "var(--bg-card)",
          color: "var(--text-primary)",
          fontSize: "var(--fs-label)",
        }}
      />
      <button
        type="submit"
        disabled={value.trim().length === 0}
        aria-label="Add card"
        title="Add card to the unplaced deck"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          borderRadius: 8,
          border: "1px solid rgba(var(--accent-true-blue-rgb), 0.35)",
          background: "rgba(var(--accent-true-blue-rgb), 0.10)",
          color: "var(--accent-true-blue)",
          fontSize: "var(--fs-label)",
          fontWeight: 600,
          letterSpacing: 1,
          cursor: value.trim().length === 0 ? "default" : "pointer",
          opacity: value.trim().length === 0 ? 0.5 : 1,
        }}
      >
        <Plus aria-hidden strokeWidth={2} style={{ width: 12, height: 12 }} />
        ADD CARD
      </button>
    </form>
  );
}

/**
 * Faint horizontal divider used in the left info rail. Mirrors the
 * legacy `1px` rule at `_legacy/wd.html` line 1081 — a single line
 * separating each rail section.
 */
function Divider() {
  return (
    <div
      aria-hidden
      style={{
        height: 1,
        flexShrink: 0,
        background: "rgba(var(--accent-true-blue-rgb), 0.2)",
        margin: 0,
      }}
    />
  );
}

/* ----------------------- Inline style helpers --------------------- */

/**
 * Eyebrow heading used for "How to use", "FUD", "Adoption" labels and
 * the unplaced-deck hint — matches the legacy `.text-cyan`/`fs-label`
 * cadence at `_legacy/wd.html` lines 1077, 1083, 1105, 1107, 1164.
 */
const SECTION_EYEBROW_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--fs-label)",
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: "uppercase",
  color: "var(--accent-sky)",
};

/**
 * Top-bar "RESET BOARD" pill — gold-tinted destructive variant that
 * sits in the same toolbar row as SAVE TO CAPTURES + HISTORY + CLOSE.
 * Matches the HandshakeBoard top-bar pill chrome for cross-tool parity.
 */
const TOP_RESET_BTN_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  background: "rgba(var(--accent-gold-rgb), 0.08)",
  border: "1px solid rgba(var(--accent-gold-rgb), 0.30)",
  borderRadius: 8,
  color: "var(--accent-gold)",
  fontSize: "var(--fs-label)",
  fontWeight: 600,
  letterSpacing: 1,
  cursor: "pointer",
  transition: "border-color 0.15s, color 0.15s, background 0.15s",
};

function onResetHover(e: ReactMouseEvent<HTMLButtonElement>): void {
  if (e.currentTarget.disabled) return;
  e.currentTarget.style.borderColor = "var(--accent-gold)";
  e.currentTarget.style.background =
    "rgba(var(--accent-gold-rgb), 0.16)";
}

function onResetLeave(e: ReactMouseEvent<HTMLButtonElement>): void {
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-gold-rgb), 0.30)";
  e.currentTarget.style.background =
    "rgba(var(--accent-gold-rgb), 0.08)";
}

/**
 * "SAVE TO CAPTURES" pill — green accent matching the legacy primary
 * action at `_legacy/wd.html` line 1064. Sits in the top toolbar so
 * the snapshot affordance reads as the highlight action.
 */
const SAVE_TO_CAPTURES_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  background: "rgba(var(--accent-green-rgb), 0.10)",
  border: "1px solid rgba(var(--accent-green-rgb), 0.35)",
  borderRadius: 8,
  color: "var(--accent-green)",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--fs-label)",
  fontWeight: 600,
  letterSpacing: 1,
  cursor: "pointer",
  transition: "background 0.2s, border-color 0.2s, color 0.2s",
};

function onSaveToCapturesHover(e: ReactMouseEvent<HTMLButtonElement>): void {
  if (e.currentTarget.disabled) return;
  e.currentTarget.style.background =
    "rgba(var(--accent-green-rgb), 0.18)";
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-green-rgb), 0.55)";
}

function onSaveToCapturesLeave(e: ReactMouseEvent<HTMLButtonElement>): void {
  e.currentTarget.style.background =
    "rgba(var(--accent-green-rgb), 0.10)";
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-green-rgb), 0.35)";
}

/**
 * Compact top-bar pill — neutral true-blue chrome used by HISTORY.
 * Matches the HandshakeBoard top-bar `TOOLBAR_BTN_STYLE` family so
 * the two facilitator tools share the same row of action chips.
 */
const TOP_BTN_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  background: "var(--bg-card)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
  borderRadius: 8,
  color: "var(--text-secondary)",
  fontSize: "var(--fs-label)",
  fontWeight: 600,
  letterSpacing: 1,
  cursor: "pointer",
  transition: "border-color 0.15s, color 0.15s, background 0.15s",
};

function onTopBtnHover(e: ReactMouseEvent<HTMLButtonElement>): void {
  if (e.currentTarget.disabled) return;
  e.currentTarget.style.borderColor = "var(--accent-true-blue)";
  e.currentTarget.style.color = "var(--accent-true-blue)";
  e.currentTarget.style.background =
    "rgba(var(--accent-true-blue-rgb), 0.06)";
}

function onTopBtnLeave(e: ReactMouseEvent<HTMLButtonElement>): void {
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-true-blue-rgb), 0.25)";
  e.currentTarget.style.color = "var(--text-secondary)";
  e.currentTarget.style.background = "var(--bg-card)";
}
